import { makeAutoObservable, runInAction } from 'mobx'
import { Icon, type IIconsVector, Typography } from 'shared/ui'
import { uiStore } from 'shared/store/uiStore'
import { logger } from 'shared/lib'
import { LINK_INTERNATIONAL_COMPLIANCE_GUIDE } from 'shared/constants/links'
import modalStore from 'shared/ui/Modal/store/modalStore'
import {
  ComplianceApi,
  type IEndUserType,
  type IInternationalNumberType,
} from 'entities/Compliance'
import type { CompliancePageStore } from 'pages/settings/pages/compliance/store/compliancePageStore'
import type { IRegisterSectionProps } from 'pages/settings/pages/compliance/ui/RegisterSection/RegisterSection'
import styles from 'pages/settings/pages/compliance/ui/styles.module.scss'
import { SettingsPath } from 'pages/settings/route/settingsPath'
import { StatusComplianceIcons } from 'pages/settings/pages/compliance/constants/statusIcon'
import { InternationalRoutes } from 'pages/settings/pages/compliance/pages/international/route/type'
import { RegulatoryBundleItem } from 'pages/settings/pages/compliance/pages/international/model/RegulatoryBundleItem'
import { EstimatedResponseInfo } from 'pages/settings/pages/compliance/ui/EstimatedResponseInfo/EstimatedResponseInfo'
import { InternationalBusinessProfileModalContent } from 'pages/settings/pages/compliance/ui/InternationalBusinessProfileModalContent/InternationalBusinessProfileModalContent'

export class InternationalPageStore {
  // Base parameters for the current iteration ('GB', 'business', 'mobile')
  private _country = 'GB'
  private _endUserType: IEndUserType = 'business'
  private _numberType: IInternationalNumberType = 'mobile'
  private _loading = false
  private _allStepsLength = 4

  constructor(private _compliancePageStore: CompliancePageStore) {
    makeAutoObservable(this)

    this.init()
  }

  get internationalRegulatoryBundlesStore() {
    return this._compliancePageStore.complianceLayerStore.internationalRegulatoryBundlesStore
  }

  get loading() {
    return this._loading
  }

  get formStep() {
    return this.internationalRegulation?.step || 0
  }

  get registerButtonText() {
    if (this.internationalRegulation?.isSubmitted) return null

    return this.formStep < 1 ? (
      'Register'
    ) : (
      <Typography ariaLabel='Continue' variant='button-medium'>
        {'Continue: '}
        <Typography
          ariaLabel='Step'
          variant='button-medium'
          color='var(--content-primary-tertiary-inverted)'
        >
          Step {this.formStep}/{this._allStepsLength}
        </Typography>
      </Typography>
    )
  }

  get internationalDataStatus() {
    if (!this.internationalRegulation) return null

    if (this.internationalInProgress) {
      return StatusComplianceIcons.condition((this.formStep / this._allStepsLength) * 100)
    }

    if (this.internationalRegulation.isStatusPending) {
      return StatusComplianceIcons.pendingReview
    }

    if (this.internationalRegulation.isStatusApproved) {
      return StatusComplianceIcons.approved
    }

    if (this.internationalRegulation.isStatusFailed) {
      return StatusComplianceIcons.reject
    }

    if (this.internationalRegulation.isStatusInCarrierReview) {
      return StatusComplianceIcons.carrierReview
    }

    if (this.internationalRegulation.isStatusUnderReview) {
      return StatusComplianceIcons.carrierReview
    }

    return null
  }

  get internationalInProgress() {
    return this.internationalRegulation?.isStatusInProgress
  }

  get learnMorePath() {
    return LINK_INTERNATIONAL_COMPLIANCE_GUIDE
  }

  get items() {
    return this.internationalRegulatoryBundlesStore.items
  }

  get internationalRegulation() {
    return this.items.find((item) => item.regulation?.numberType === 'mobile')
  }

  get internationalLabel() {
    if (this._country === 'GB') {
      return 'UK: Regulatory bundle'
    }

    return ''
  }

  get internationalFlagIcon() {
    if (this._country === 'GB') {
      return 'flagUK'
    }

    return ''
  }

  get internationalRegisterSectionProps(): IRegisterSectionProps {
    return {
      title: (
        <Typography ariaLabel='InternationalRegister' tag='div' className={styles.sectionTitle}>
          <Typography
            ariaLabel='InternationalRegisterText'
            variant='body-lg-medium'
            className={styles.sectionTitleText}
          >
            {this.internationalLabel}
          </Typography>
          {this.internationalFlagIcon && (
            <Icon className={styles.flag} icon={this.internationalFlagIcon} />
          )}
        </Typography>
      ),
      titleType: 'body-lg-medium',
      desc: (
        <Typography
          ariaLabel='InternationalRegisterDesc'
          variant='body-md-regular'
          tag='div'
          className={styles.customDesc}
        >
          Register your business with carriers to send SMS{' '}
          {this.internationalInProgress ? '' : 'and make'}
          <div className={styles.descSubtext}>
            {this.internationalInProgress ? 'and make calls' : 'calls'}
            <Icon icon='arrowRight' className={styles.arrow} /> to United Kingdom.
            {!this.internationalInProgress && (
              <a
                href={this.learnMorePath}
                target='_blank'
                rel='noreferrer'
                className={styles.learnMoreLink}
              >
                <Typography
                  ariaLabel='TermsAndPrivacy_link'
                  variant='body-md-medium'
                  className={styles.learnMoreText}
                >
                  Learn more
                </Typography>
              </a>
            )}
          </div>
        </Typography>
      ),
      learnMoreLink: this.internationalInProgress ? this.learnMorePath : undefined,
      descContentClassName: this.internationalInProgress ? 'newLineLink' : undefined,
      isSecondStep: this.internationalRegulation?.isSubmitted,
      actionButton: this.registerButtonText
        ? {
            text: this.registerButtonText,
            onClick: () => {
              if (this.internationalInProgress) {
                let newPath

                if (this.formStep === 1) {
                  newPath = InternationalRoutes.businessInfo
                }
                if (this.formStep === 2) {
                  newPath = InternationalRoutes.businessAddress
                }

                if (this.formStep === 3) {
                  newPath = InternationalRoutes.contactInfo
                }

                if (this.formStep === 4) {
                  newPath = InternationalRoutes.finalDetails
                }

                if (newPath) {
                  uiStore.changeRoute({
                    path: `/${SettingsPath.compliance.international.root}/${newPath}`,
                  })
                }
              } else {
                this.goToBusinessInfo()
              }
            },
          }
        : undefined,
    }
  }

  goToBusinessInfo = () => {
    uiStore.changeRoute({
      path: `/${SettingsPath.compliance.international.businessInfo}`,
    })
  }

  openSummaryModal = () => {
    const modalId = 'international_business_profile_summary'

    modalStore.addModal({
      id: modalId,
      title: this.internationalLabel,
      width: 540,
      showCloseButton: false,
      hiddenContent: true,
      paddingContent: '0',
      ModalContent: InternationalBusinessProfileModalContent,
      ModalContentProps: {
        statusData: <EstimatedResponseInfo iconData={this.internationalDataStatus} />,
        closeModal: () => modalStore.closeModal(modalId),
        editBusinessProfile: () => {
          this.goToBusinessInfo()
          this.hideRegistrationErrorsBanner()
        },
        internationalPageStore: this,
      },
    })
  }

  init = async () => {
    this.internationalRegulatoryBundlesStore.setRegulatoryBundleCountry(this._country)

    runInAction(() => {
      this._loading = true
    })

    try {
      const { data } = await ComplianceApi.getInternationalRegulatoryBundle({
        country: this._country,
        endUserType: this._endUserType,
        numberType: this._numberType,
      })

      if ('data' in data && data.data) {
        runInAction(() => {
          data.data?.forEach((item) => {
            this.internationalRegulatoryBundlesStore.setItem(new RegulatoryBundleItem(item))
          })
        })
      }
    } catch (e) {
      logger.error(e)
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  hideRegistrationErrorsBanner = () => {
    this.internationalRegulatoryBundlesStore.hideErrorsBanner()
  }

  getRegistrationAuthorityCountries = (key: string) => {
    return [
      {
        label: 'UK: Company Registration Number (CRN)',
        id: 'UK:CRN',
        icon: 'flagUK' as IIconsVector,
      },
      {
        label: 'US: Employer Identification Number (EIN)',
        id: 'US:EIN',
        icon: 'flagUSA' as IIconsVector,
      },
      {
        label: 'CA: Canadian Business Number (CBN)',
        id: 'CA:CBN',
        icon: 'flagCanada' as IIconsVector,
      },
      {
        label: 'AU: Australian Company Number (ACN)',
        id: 'AU:ACN',
        icon: 'flagAU' as IIconsVector,
      },
      {
        label: 'Other',
        id: 'OTHER',
        icon: 'globe' as IIconsVector,
      },
    ].find((item) => item.id === key)
  }

  getRegistrationAuthorityInfo = (key: string) => {
    return [
      {
        label: 'CRN',
        id: 'UK:CRN',
      },
      {
        label: 'EIN',
        id: 'US:EIN',
      },
      {
        label: 'CBN',
        id: 'CA:CBN',
      },
      {
        label: 'ACN',
        id: 'AU:ACN',
      },
      {
        label: 'Business Registration Number',
        id: 'OTHER',
      },
    ].find((item) => item.id === key)
  }
}
